import { ProductTier } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/product.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useAppFlagStore } from '@frontend/shared';
import { canUseCallIntelPremiumFeatures } from '../utils/call-intel-subscription-utils';
import { useCallIntelFeatureAccess } from './use-call-intel-feature-access';
import { useCallIntelSubscription } from './use-call-intel-subscription';
import { useHasCallIntelAclAccess } from './use-has-call-intel-acl-access';

type UseCallIntelColumnsAccessParams = {
  locationIds: string[];
};

type UseCallIntelColumnsAccess = {
  showCallIntelInsights: boolean;
  showServiceQuality: boolean;
  hasCallIntelPremiumFeatures: boolean;
  isDemoUser: boolean;
  currentPlan: ProductTier;
  locationPlanMap: Record<string, ProductTier>;
};

export const useCallIntelColumnsAccess = ({
  locationIds,
}: UseCallIntelColumnsAccessParams): UseCallIntelColumnsAccess => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const isServiceQualityEnabled = getFeatureFlagValue({ flagName: 'enable-service-quality', locationIds });

  const {
    hasPermission: hasCallIntelInsightsPermission,
    isFlagEnabled: isCallIntelInsightsFlagEnabled,
    isDemoUser,
  } = useCallIntelFeatureAccess({
    permissions: [Permission.CALL_CLASSIFICATION_READ],
    allowDemoOverride: true,
  });

  const { currentPlan, locationPlanMap } = useCallIntelSubscription({
    locationIds,
  });

  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const showCallIntelInsights =
    hasCallIntelInsightsPermission &&
    isCallIntelInsightsFlagEnabled &&
    currentPlan !== ProductTier.PRODUCT_TIER_UNSPECIFIED;

  const hasCallIntelPremiumFeatures = showCallIntelInsights && canUseCallIntelPremiumFeatures(currentPlan);

  const showServiceQuality = showCallIntelInsights && isServiceQualityEnabled && hasServiceQualityAccess;

  return {
    showCallIntelInsights,
    showServiceQuality,
    hasCallIntelPremiumFeatures,
    isDemoUser,
    currentPlan,
    locationPlanMap,
  };
};
